import { PaymentProviderEnum } from '@pepperhq/types-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, styled, Tab, Tabs } from '@mui/material';

import { MerchantTitle } from 'app/MerchantTitle';
import { MuiAppBar } from 'lib/appbar/MuiAppBar';
import { ActivityTab } from './activity-tab';
import { OrdersTab } from './orders-tab';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { isDefined } from 'lib/typeInference';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';

const StyledTabsBar = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: '48px',
    left: 0,
    zIndex: 1,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`
}));

enum ActivityScreenTabs {
    Orders = 'orders',
    Actions = 'actions'
}

export const ActivityPage: React.FC = () => {
    const { t } = useTranslation();
    const { logUserEvent } = useGAHelpers();
    const settings = useSelector((state: ApplicationState) => state.settings);
    const [value, setValue] = React.useState<ActivityScreenTabs>();

    const isPaymentProviderSet = React.useMemo(
        () =>
            isDefined(settings?.settings?.paymentProvider) &&
            settings?.settings?.paymentProvider !== PaymentProviderEnum.NONE,
        [settings]
    );

    React.useEffect(() => {
        if (isDefined(settings?.settings?.paymentProvider)) {
            const isSet = settings?.settings?.paymentProvider !== PaymentProviderEnum.NONE;
            setValue(isSet ? ActivityScreenTabs.Orders : ActivityScreenTabs.Actions);
        }
    }, [settings?.settings?.paymentProvider]);

    const handleChange = React.useCallback(
        (_: React.SyntheticEvent, newValue: ActivityScreenTabs) => {
            setValue(newValue);
            const eventName =
                newValue === ActivityScreenTabs.Orders
                    ? EGAEventName.ActivityOrdersTabSelected
                    : EGAEventName.ActivityActionsTabSelected;
            logUserEvent(eventName);
        },
        [logUserEvent]
    );

    return (
        <Box position="relative" height="100%" width="100%" className="hidden-scroll">
            <MerchantTitle title={t('MAIN_MENU_ACTIVITY')} />
            <MuiAppBar title={t('MAIN_MENU_ACTIVITY')} />
            {isPaymentProviderSet && (
                <StyledTabsBar>
                    <Tabs value={value} onChange={handleChange} variant="fullWidth" centered>
                        <Tab value={ActivityScreenTabs.Orders} label={t('ACTIVITY_ORDERS')} />
                        <Tab value={ActivityScreenTabs.Actions} label={t('ACTIVITY_ACTIONS')} />
                    </Tabs>
                </StyledTabsBar>
            )}
            {value === ActivityScreenTabs.Orders && <OrdersTab />}
            {value === ActivityScreenTabs.Actions && <ActivityTab />}
        </Box>
    );
};
